/**
 * Phase 20 — the editor's "Right now": the stop-loss being edited, judged on
 * the layer's sends of today with the stats already collected (POST
 * preview, zero writes, no tracker call). The rule and its guards are
 * debounced together; the previous answer stays while the next one loads.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { type DropGenAutoSendConfig, type DropGenStopLossPreview, stopLossOn } from '@trafficpulse/shared';

import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { api } from '@/services/api';

import { dropGeneratorKeys } from '../../../useDropGenerator';

export function useStopLossPreview(id: string | null, config: DropGenAutoSendConfig) {
  // One stable string: an object would be new on every render and restart the debounce forever.
  const liveKey = JSON.stringify({
    stopLossJson: config.stopLossJson,
    stopLossMinSampleSent: config.stopLossMinSampleSent,
    stopLossDelayMin: config.stopLossDelayMin,
  });
  const bodyKey = useDebouncedValue(liveKey, 400);
  const body = useMemo(() => JSON.parse(bodyKey) as Record<string, unknown>, [bodyKey]);
  const query = useQuery({
    queryKey: [...dropGeneratorKeys.all, 'stop-loss-preview', id, bodyKey] as const,
    queryFn: async () => {
      const res = await api.post<{ data: DropGenStopLossPreview }>(`/drop-generator/containers/${id}/autosend/preview`, body);
      return res.data.data;
    },
    enabled: Boolean(id) && stopLossOn(config),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: false,
  });
  return { ...query, pending: liveKey !== bodyKey };
}
